import { InlineNotification } from '@carbon/react';
import type { ReconcileCreateResponse } from 'types/api-responses';
import { autoMatchBasisSubtitle } from './auto-match-basis';
import { ConfirmedMatchBanner } from './confirmed-match-banner';
import { DocumentMatchingBanner } from './document-matching-banner';
import { ReconcileResultBanner } from './reconcile-result-banner';
import type { PartyState } from './party-status';
import type { StructuredPartyDetail } from './types';
import style from './party-detail.module.scss';

interface PartyStatusBannersProps {
  detail: StructuredPartyDetail;
  state: PartyState;
  reconcileUndoAvailable: boolean;
  reconcileResult: ReconcileCreateResponse | null;
  reconcileDetailsOpen: boolean;
  onToggleReconcileDetails: () => void;
  markCorrectAlias: boolean;
  confidenceStale: boolean;
  onDismissConfidenceStale: () => void;
}

/**
 * The banners above Match Review: how the party was resolved, plus the
 * transient outcome of this session's last reconcile or Mark as Correct.
 *
 * @param {PartyStatusBannersProps} props - The party, its state, and the last reconcile or Mark as Correct outcome.
 * @return {JSX.Element} The status banners.
 */
export function PartyStatusBanners({
  detail,
  state,
  reconcileUndoAvailable,
  reconcileResult,
  reconcileDetailsOpen,
  onToggleReconcileDetails,
  markCorrectAlias,
  confidenceStale,
  onDismissConfidenceStale,
}: PartyStatusBannersProps) {
  const {
    showConfirmedMatchBanner,
    isProvisional,
    resolvedViaDedupe,
    resolvedViaReconcile,
    isUnresolvable,
  } = state;
  return (
    <>
      {detail.autoMatch?.outcome === 'auto_confirmed' && (
        <InlineNotification
          kind="success"
          lowContrast
          hideCloseButton
          title="Auto-confirmed by Autoprovision —"
          subtitle={autoMatchBasisSubtitle(detail.autoMatch.basisLabel)}
          className={style.provisionalBanner}
        />
      )}
      {reconcileResult && (
        <ReconcileResultBanner
          result={reconcileResult}
          detailsOpen={reconcileDetailsOpen}
          onToggleDetails={onToggleReconcileDetails}
        />
      )}
      {markCorrectAlias && (
        <InlineNotification
          kind="success"
          lowContrast
          hideCloseButton
          title="Marked as correct via alias —"
          subtitle="The names differ, so the staged entity was linked to the selected one as an alias (its name is preserved) rather than merged."
          className={style.provisionalBanner}
        />
      )}
      {confidenceStale && (
        <InlineNotification
          kind="info"
          lowContrast
          title="Match confidence scores may no longer reflect recent changes."
          subtitle="You can re-run matching at any time to update them."
          onCloseButtonClick={onDismissConfidenceStale}
          className={style.provisionalBanner}
        />
      )}
      {showConfirmedMatchBanner && detail.confirmedEntity && (
        <ConfirmedMatchBanner
          entity={detail.confirmedEntity}
          provisional={isProvisional}
        />
      )}
      {resolvedViaDedupe && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={
            resolvedViaReconcile
              ? 'Resolved via Reconcile —'
              : 'Resolved via Entity Deduplication —'
          }
          subtitle={
            resolvedViaReconcile
              ? reconcileUndoAvailable
                ? 'This party was reconciled into the entity above from this document. Use “Undo Reconcile” on the party to reverse it.'
                : 'This party was reconciled into the entity above from this document. Manage the resolution on the Entities screen.'
              : 'This party was merged into the entity above from the Entities screen.'
          }
          className={style.provisionalBanner}
        />
      )}
      {isUnresolvable && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Unresolvable —"
          subtitle={`This party’s match ends at a retired entity, so it can’t be resolved from this document. ${unresolvableNextStep(state)}`}
          className={style.provisionalBanner}
        />
      )}
    </>
  );
}

/**
 * What an examiner can do about an unresolvable party: Remove Correct Status
 * when it is on offer and allowed, else why it is blocked or where to go.
 *
 * @param {PartyState} state - The party state.
 * @return {string} The sentence.
 */
function unresolvableNextStep({
  canRevert,
  revertAllowed,
  revertDisabledReason,
}: PartyState): string {
  if (canRevert && revertAllowed) {
    return 'Use Remove Correct Status to return it to Not Started.';
  }
  return (
    (canRevert && revertDisabledReason) ||
    'Manage the resolution on the Entities screen.'
  );
}

interface MatchReviewIntroProps {
  state: PartyState;
  rerunPending: boolean;
}

/**
 * The Match Review heading, a re-run banner, and guidance for the party's state.
 *
 * @param {MatchReviewIntroProps} props - The party state and whether a re-run is pending.
 * @return {JSX.Element} The Match Review intro.
 */
export function MatchReviewIntro({
  state,
  rerunPending,
}: MatchReviewIntroProps) {
  const { isRemoved, isUnresolvable, isProvisional, isComplete } = state;
  return (
    <>
      <p className={style.matchesHeader}>Match Review</p>
      {rerunPending && (
        <DocumentMatchingBanner
          title="Generating fresh suggestions…"
          message="Matching is running. New suggestions will appear here when it finishes."
        />
      )}
      <p className={style.matchesIntro}>
        {isRemoved ? (
          'This party has been removed. The matches below are no longer applicable.'
        ) : isUnresolvable ? (
          'This match can’t be resolved from this document.'
        ) : isProvisional ? (
          'This match is in progress. The selected entity is an imported entity and requires resolution to a canonical individual or organization entity.'
        ) : isComplete ? (
          'This match has been confirmed. The confirmed entity is highlighted below. You can review all matches in context.'
        ) : (
          <>
            If the party is irrelevant, <strong>Remove</strong> it. Otherwise,
            use <strong>Edit Entity Details</strong> to fix extraction errors —
            then <strong>Re-run Match</strong> after significant changes. Check{' '}
            <strong>Match Details</strong> before using{' '}
            <strong>Change/Edit Match</strong> to search for missed variations.
            Confirm a match and click <strong>Mark as Correct</strong>, or use{' '}
            <strong>Add as New</strong> if no match exists.
          </>
        )}
      </p>
    </>
  );
}
